import { apiClient } from "@/config/api.config";

/**
 * Service to handle admin approval requests for orders
 */
class RequestOrderService {
  /**
   * Get all approval requests with filters and pagination
   * @param {Object} params - Query parameters (status, priority, page, limit, sortBy, sortOrder, search)
   * @returns {Promise<Object>}
   */
  async getAllusersApprovalRequests(params = {}) {
    try {
      const res = await apiClient.get("/admin/orders/approval-requests", {
        params,
      });
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get dashboard statistics for approval requests
   * @returns {Promise<Object>}
   */
  async getDashboardStats() {
    try {
      const res = await apiClient.get(
        "/admin/orders/approval-requests/dashboard"
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Approve an approval request with custom pricing
   * @param {string} requestId
   * @param {Object} data - { customBuyPrice, notes, paymentMethod, paymentReference, processImmediately }
   * @returns {Promise<Object>}
   */
  async approveRequest(requestId, data) {
    try {
      const res = await apiClient.post(
        `/admin/orders/approval-requests/${requestId}/approve`,
        data
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Reject an approval request
   * @param {string} requestId
   * @param {Object} data - { notes }
   * @returns {Promise<Object>}
   */
  async rejectRequest(requestId, data) {
    try {
      const res = await apiClient.post(
        `/admin/orders/approval-requests/${requestId}/reject`,
        data
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Confirm offline payment received and finalize request
   * @param {string} requestId
   * @param {Object} data - { paymentReference, notes }
   * @returns {Promise<Object>}
   */
  async confirmOfflinePayment(requestId, data) {
    try {
      const res = await apiClient.post(
        `/admin/orders/approval-requests/${requestId}/confirm-offline-payment`,
        data
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Manually process an already approved request
   * @param {string} requestId
   * @param {Object} data - { paymentReference, notes }
   * @returns {Promise<Object>}
   */
  async processApprovedRequest(requestId, data) {
    try {
      const res = await apiClient.post(
        `/admin/orders/approval-requests/${requestId}/process`,
        data
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Create a new approval request
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async createApprovalRequest(data) {
    try {
      const res = await apiClient.post("/portfolio/approval-requests", data);
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  async getApprovalRequests(params = {}) {
    try {
      const res = await apiClient.get("/portfolio/user-approval-requests", {
        params,
      });
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  /**
   * Get approval request details
   * @param {string} requestId
   * @returns {Promise<Object>}
   */
  async getApprovalRequestDetails(requestId) {
    try {
      const res = await apiClient.get(
        `/portfolio/approval-requests/${requestId}`
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Cancel an approval request
   * @param {string} requestId
   * @returns {Promise<Object>}
   */
  async cancelApprovalRequest(requestId) {
    try {
      const res = await apiClient.put(
        `/portfolio/approval-requests/${requestId}/cancel`
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Mark messages as read for an approval request
   * @param {string} requestId
   * @returns {Promise<Object>}
   */
  async markMessagesAsRead(requestId) {
    try {
      const res = await apiClient.put(
        `/portfolio/approval-requests/${requestId}/messages/read`
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Initiate online payment for an approval request (user-facing)
   * @param {string} requestId
   */
  async initiateOnlinePayment(requestId) {
    try {
      const res = await apiClient.post(
        `/portfolio/orders/approval-requests/${requestId}/initiate-online-payment`
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Verify online investment payment after Razorpay checkout
   * @param {Object} payload - { razorpay_order_id, razorpay_payment_id, razorpay_signature }
   */
  async verifyOnlineInvestmentPayment(payload) {
    try {
      const res = await apiClient.post(
        `/transactions/verify-online-investment-payment`,
        payload
      );
      return res.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Handle API errors
   * @private
   */
  _handleError(error) {
    // Customize error handling as needed
    return error?.response?.data || error;
  }
}

export const requestOrderService = new RequestOrderService();
